import React, { useEffect, useState } from 'react';
import {
  IWidgetGridItem,
  IWidgetGridItemLayout,
  IWidgetWrapper,
} from './types';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';

import './widgetsGrid.css';
import { useTranslation } from 'react-i18next';
import PlaceholderWidget from './Placeholder/Placeholder';
import { logger } from '../../helpers/logger';
import { Debug } from '../Debug/Debug';

interface WidgetsGridProps {
  resizableActive: boolean;
  gridColsPerBreakpoint: {
    xl: number;
    lg: number;
    md: number;
    sm: number;
    xs: number;
  };

  onItemResize?: (id: string, w: number, h: number) => void;
  onItemMove?: (id: string, position: number, ids: string[]) => void;
  onItemFocus?: (id: string) => void;
  widgets: IWidgetWrapper[];
}

export const WidgetsGrid = ({
  gridColsPerBreakpoint,
  widgets,
  resizableActive = true,
  onItemResize,
  onItemMove,
  onItemFocus,
}: WidgetsGridProps) => {
  const { t } = useTranslation();

  const { clientWidth } = document.documentElement;

  const gridBreakpoints = {
    xl: clientWidth < 5120 && clientWidth > 2220 ? clientWidth - 2 : 2220,
    lg: clientWidth < 2220 && clientWidth > 1780 ? clientWidth - 2 : 1780,
    md: clientWidth < 1780 && clientWidth > 1340 ? clientWidth - 2 : 1340,
    sm: clientWidth < 1340 && clientWidth > 900 ? clientWidth - 2 : 900,
    xs: clientWidth,
  };

  const maxHeight = 2;

  const [widgetsGrid, setWidgetsGrid] = useState<IWidgetGridItem[]>([]);
  const [currentCols, setCurrentCols] = useState<number>(0);
  const [currentGridBreakpoint, setCurrentGridBreakpoint] = useState<number>(0);
  const [gridLayoutWidth, setGridLayoutWidth] = useState<number>(clientWidth);

  const [selectedWidgetId, setSelectedWidgetId] = useState<string>('');

  // Handle grid breakpoints
  useEffect(() => {
    const currentGridBreakpoint = Object.entries(gridBreakpoints).reduce(
      (gridBreakpoint, currBreakpoint) =>
        document.documentElement.clientWidth > gridBreakpoint[1]
          ? gridBreakpoint
          : currBreakpoint
    );

    setCurrentCols(gridColsPerBreakpoint[currentGridBreakpoint[0]]);
    setCurrentGridBreakpoint(currentGridBreakpoint[1]);

    const updateGridLayoutWidth = () => {
      setGridLayoutWidth(document.documentElement.clientWidth);
    };

    window.addEventListener('resize', updateGridLayoutWidth);

    return () => {
      window.removeEventListener('resize', updateGridLayoutWidth);
    };
  }, [clientWidth]);

  // Create/Update Widget Grid
  useEffect(() => {
    if (currentCols === 0) {
      return;
    }

    logger.log('🟡 WidgetGrid:widgets', widgets);

    // Create WidgetGrid from widgets
    const widgetsGrid = prepareWidgetsGrid(widgets);
    setWidgetsGrid(widgetsGrid);
  }, [currentCols, widgets]);

  const prepareWidgetsGrid = (widgets: IWidgetWrapper[]): IWidgetGridItem[] => {
    let x = 0;
    let y = 0;
    let xPos = 0;
    let yPos = 0;
    let availableColsInRow = currentCols;
    let emptySpaces: { x: number; y: number }[] = [];

    return widgets.map((widgetWrapper, index) => {
      const w = Math.min(widgetWrapper.width, currentCols);

      const h = Math.min(widgetWrapper.height, maxHeight);

      // logger.log('pwg:0', {
      //   w,
      //   h,
      // });
      //
      // logger.log('pwg:0', widgetWrapper.id, {
      //   x,
      //   y,
      //   xPos,
      //   yPos,
      //   av: availableColsInRow,
      //   es: emptySpaces.length,
      // });

      if (
        emptySpaces.length &&
        emptySpaces.find((s) => currentCols - s.x >= w)
      ) {
        const index = emptySpaces.findIndex((s) => currentCols - s.x >= w);
        x = emptySpaces[index].x;
        y = emptySpaces[index].y;
        emptySpaces.splice(index, 1);

        // logger.log('pwg:1A', widgetWrapper.id, {
        //   x,
        //   y,
        //   xPos,
        //   yPos,
        //   av: availableColsInRow,
        //   es: emptySpaces.length,
        // });
      } else {
        if (index > 0 && availableColsInRow <= 0) {
          availableColsInRow = currentCols;
          yPos++;
          xPos = 0;
        }

        // logger.log('pwg:1B', widgetWrapper.id, {
        //   x,
        //   y,
        //   xPos,
        //   yPos,
        //   av: availableColsInRow,
        //   es: emptySpaces.length,
        // });

        if (w <= availableColsInRow) {
          x = xPos;
          xPos = xPos + w;
          y = yPos;
          availableColsInRow = availableColsInRow - w;
        } else {
          emptySpaces.push({ x: x + w, y });
          x = 0;
          xPos = w;
          y = ++yPos;
          availableColsInRow = currentCols - w;
        }

        // logger.log('pwg:2', widgetWrapper.id, {
        //   x,
        //   y,
        //   xPos,
        //   yPos,
        //   av: availableColsInRow,
        //   es: emptySpaces.length,
        // });
      }

      // logger.log('pwg:3', widgetWrapper.id, {
      //   x,
      //   y,
      //   xPos,
      //   yPos,
      //   av: availableColsInRow,
      //   es: emptySpaces.length,
      // });

      return {
        id: widgetWrapper.id,
        locked: widgetWrapper?.locked ?? true,
        children: widgetWrapper.children,
        layout: {
          i: widgetWrapper.id,
          x,
          y,
          w,
          h,
          minW: 1,
          maxW: currentCols,
          minH: 1,
          maxH: maxHeight,
        },
      };
    });
  };

  const handleBreakpointChange = (newCols: number) => {
    setCurrentCols(newCols);
  };

  const handleResizeStop = (
    layout: IWidgetGridItemLayout[],
    oldItem: IWidgetGridItemLayout,
    newItem: IWidgetGridItemLayout
  ) => {
    if (oldItem.w === newItem.w && oldItem.h === newItem.h) {
      return;
    }

    logger.log('WidgetGrid:handleResize', oldItem, newItem);
    onItemResize?.(newItem.i, newItem.w, newItem.h);
  };

  const handleDragStop = (
    layout: IWidgetGridItemLayout[],
    oldItem: IWidgetGridItemLayout,
    newItem: IWidgetGridItemLayout
  ) => {
    if (oldItem.x === newItem.x && oldItem.y === newItem.y) {
      return;
    }

    const sortedLayout = [...layout].sort(
      (a, b) => a.y * currentCols + a.x - (b.y * currentCols + b.x)
    );

    const index = sortedLayout.findIndex((item) => item.i === newItem.i);

    logger.log('WidgetGrid:handleDragStop', layout, sortedLayout, index);

    onItemMove?.(
      newItem.i,
      index,
      sortedLayout.map((item) => item.i)
    );
  };

  const handleSelectWidget = (id) => {
    logger.log('WidgetGrid:handleSelectWidget', id, selectedWidgetId);
    setSelectedWidgetId(id);
    onItemFocus?.(id);
  };
  const resizeHandle = resizableActive ? (
    <div
      className={`absolute flex w-5 h-5 bottom-3.5 right-3.5 cursor-pointer overflow-hidden hidden-from-snapshot`}
    >
      <div className="flex transform -rotate-45 -ml-1 mt-1">
        <span className="material-icons text-lg w-2 h-2">arrow_left</span>
        <span className="material-icons text-lg w-2 h-2">arrow_right</span>
      </div>
    </div>
  ) : (
    <div></div>
  );

  return (
    <>
      {!!widgetsGrid.length ? (
        <>
          <Debug>
            <>
              {`VW: ${gridLayoutWidth}, GridWidth: ${currentGridBreakpoint}, Cols: ${currentCols}, Widgets: ${widgetsGrid.length}`}
            </>
          </Debug>
          <ResponsiveGridLayout
            className="layout"
            rowHeight={480}
            layouts={{
              xl: widgetsGrid.map(({ layout }) => layout),
              lg: widgetsGrid.map(({ layout }) => layout),
              md: widgetsGrid.map(({ layout }) => layout),
              sm: widgetsGrid.map(({ layout }) => layout),
              xs: widgetsGrid.map(({ layout }) => layout),
            }}
            compactType={'vertical'}
            breakpoints={gridBreakpoints}
            cols={gridColsPerBreakpoint}
            margin={[19, 19]}
            draggableHandle=".WidgetsGridDragHandle"
            width={currentGridBreakpoint + 1}
            resizeHandles={['se']}
            resizeHandle={resizeHandle}
            onBreakpointChange={(newBreakpoint, newCols) =>
              handleBreakpointChange(newCols)
            }
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
          >
            {widgetsGrid.map(({ id, children }, index) => (
              <div
                className={`relative flex flex-col`}
                style={{ zIndex: widgetsGrid.length - index }}
                key={id}
                onClick={() => handleSelectWidget(id)}
              >
                {children}
              </div>
            ))}
          </ResponsiveGridLayout>
        </>
      ) : (
        <div className={'ml-4 my-4 -mr-4'}>
          <PlaceholderWidget
            width={3}
            height={1}
            title={t('widgetsGrid.noWidgetTitle')}
            subtitle={t('widgetsGrid.noWidgetSubtitle')}
          />
        </div>
      )}
    </>
  );
};
